import { type ChangeEvent, type FormEvent, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import type { ShareholderPayload } from "../../config/api";
import type { ShareholderIdentification as Shareholder } from "../../services/companies/generated/model";
import "./AddShareholderModal.css";

interface AddShareholderModalProps {
  isOpen: boolean;
  onClose: () => void;
  editShareholder?: Shareholder | null;
  onSave: (data: ShareholderPayload) => void;
  isSubmitting?: boolean;
  errorMessage?: string | null;
}

const emptyForm: ShareholderPayload = {
  name: "",
  ownershipPercent: "",
  nationality: "",
};

const AddShareholderModal = ({
  isOpen,
  onClose,
  editShareholder = null,
  onSave,
  isSubmitting = false,
  errorMessage = null,
}: AddShareholderModalProps) => {
  const { t } = useTranslation(["company", "common"]);
  const [form, setForm] = useState<ShareholderPayload>(emptyForm);

  useEffect(() => {
    if (!isOpen) return;
    if (editShareholder) {
      setForm({
        name: editShareholder.name ?? "",
        ownershipPercent: editShareholder.ownershipPercent ?? "",
        nationality: editShareholder.nationality ?? "",
      });
    } else {
      setForm(emptyForm);
    }
  }, [isOpen, editShareholder]);

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    onSave(form);
  };

  if (!isOpen) return null;

  const isEdit = Boolean(editShareholder?.id);

  return (
    <div className="modal-overlay shareholder-modal-overlay" onClick={onClose}>
      <div className="modal-content shareholder-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header shareholder-modal-header">
          <h2 className="modal-title">{isEdit ? t("addShareholder.editTitle") : t("addShareholder.addTitle")}</h2>
          <button type="button" className="modal-close" onClick={onClose} aria-label={t("common:actions.close")}>
            <i className="fa-solid fa-xmark" />
          </button>
        </div>

        <form className="shareholder-modal-form" onSubmit={handleSubmit}>
          <div className="shareholder-modal-field">
            <label className="shareholder-modal-label" htmlFor="shareholder-name">
              {t("addShareholder.name")}
            </label>
            <input
              id="shareholder-name"
              name="name"
              type="text"
              className="shareholder-modal-input"
              placeholder={t("addShareholder.namePlaceholder")}
              value={form.name}
              onChange={handleChange}
              required
            />
          </div>
          <div className="shareholder-modal-field">
            <label className="shareholder-modal-label" htmlFor="shareholder-ownershipPercent">
              {t("addShareholder.ownership")}
            </label>
            <input
              id="shareholder-ownershipPercent"
              name="ownershipPercent"
              type="text"
              className="shareholder-modal-input"
              placeholder={t("addShareholder.ownershipPlaceholder")}
              value={form.ownershipPercent}
              onChange={handleChange}
              required
            />
          </div>
          <div className="shareholder-modal-field">
            <label className="shareholder-modal-label" htmlFor="shareholder-nationality">
              {t("addShareholder.nationality")}
            </label>
            <input
              id="shareholder-nationality"
              name="nationality"
              type="text"
              className="shareholder-modal-input"
              placeholder={t("addShareholder.nationalityPlaceholder")}
              value={form.nationality}
              onChange={handleChange}
              required
            />
          </div>
          {errorMessage && (
            <p className="shareholder-modal-error" role="alert">
              {errorMessage}
            </p>
          )}

          <div className="shareholder-modal-footer">
            <button
              type="button"
              className="shareholder-modal-btn shareholder-modal-btn--cancel"
              onClick={onClose}
              disabled={isSubmitting}
            >
              {t("common:actions.cancel")}
            </button>
            <button
              type="submit"
              className="shareholder-modal-btn shareholder-modal-btn--primary"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin" aria-hidden />
                  {isEdit ? t("addShareholder.updating") : t("addShareholder.adding")}
                </>
              ) : isEdit ? (
                t("addShareholder.updateSubmit")
              ) : (
                t("addShareholder.addSubmit")
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AddShareholderModal;
